import { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle } from 'lucide-react';
import { get, post } from '@/lib/api';

interface PolicySection {
  title: string;
  content: string;
  order: number;
}

interface Policy {
  id: string;
  name: string;
  category: string;
  acknowledgement_type: 'view_only' | 'acknowledge' | 'attestation';
  scroll_to_end_required: boolean;
  typed_name_required: boolean;
  current_version: number;
}

interface AttestationQuestion {
  question: string;
  answer_type: 'yes_no' | 'short_answer';
  required: boolean;
}

type Step = 1 | 2 | 3 | 'done';

export function PolicySignPage() {
  const { id: policyId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState<Policy | null>(null);
  const [sections, setSections] = useState<PolicySection[]>([]);
  const [attestationQuestions] = useState<AttestationQuestion[]>([]);
  const [step, setStep] = useState<Step>(1);

  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [typedName, setTypedName] = useState('');
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!policyId) return;
    get<{ policy: Policy; sections: PolicySection[] }>(`legal/policies/${policyId}/for-signing`)
      .then(d => { setPolicy(d.policy); setSections(d.sections); })
      .catch(() => navigate('/policies'))
      .finally(() => setLoading(false));
  }, [policyId, navigate]);

  const handleScroll = () => {
    if (!scrollRef.current) return;
    const el = scrollRef.current;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 40) setScrolledToEnd(true);
  };

  const canSubmitStep2 = () => {
    if (!acknowledged) return false;
    if (policy?.typed_name_required && typedName.trim().length < 2) return false;
    if (policy?.acknowledgement_type === 'attestation') {
      return attestationQuestions.every((q, i) => !q.required || !!answers[i]);
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!policyId) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await post<{ acknowledgement: { id: string; signed_at: string } }>(`legal/policies/${policyId}/acknowledge`, {
        typed_name: typedName || null,
        scrolled_to_end: scrolledToEnd,
        attestation_answers: attestationQuestions.map((q, i) => ({ question: q.question, answer: answers[i] ?? '' })),
      });
      setSignedAt(result.acknowledgement.signed_at);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (step !== 'done') return;
    const t = setTimeout(() => navigate('/policies'), 3000);
    return () => clearTimeout(t);
  }, [step, navigate]);

  if (loading) return <div className="text-muted-foreground text-sm">Loading policy…</div>;
  if (!policy) return null;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/policies')} className="text-muted-foreground hover:text-foreground">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Sign Policy</p>
          <h1 className="text-lg font-bold text-foreground">{policy.name}</h1>
        </div>
      </div>

      {/* Step indicators */}
      {step !== 'done' && (
        <div className="flex items-center gap-2 text-xs">
          {(['Read', 'Acknowledge', 'Confirm'] as const).map((label, i) => {
            const s = (i + 1) as 1 | 2 | 3;
            const active = step === s;
            const done = (step as number) > s;
            return (
              <>
                <span key={label} className={`flex items-center gap-1 font-medium ${active ? 'text-amber-500' : done ? 'text-green-500' : 'text-muted-foreground'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${active ? 'bg-amber-500 text-foreground' : done ? 'bg-green-500 text-foreground' : 'bg-surface-3 text-muted-foreground'}`}>
                    {done ? '✓' : s}
                  </span>
                  {label}
                </span>
                {i < 2 && <span className="flex-1 h-px bg-border" />}
              </>
            );
          })}
        </div>
      )}

      {/* Step 1: Read */}
      {step === 1 && (
        <div className="glass rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-border">
            <p className="text-xs text-muted-foreground">Version {policy.current_version} · {policy.category}</p>
            {policy.scroll_to_end_required && !scrolledToEnd && (
              <p className="text-xs text-amber-500 mt-1">Scroll to the bottom to continue.</p>
            )}
          </div>
          <div ref={scrollRef} onScroll={handleScroll} className="px-5 py-4 space-y-4 overflow-y-auto" style={{ maxHeight: '60vh' }}>
            {sections.map((sec, i) => (
              <div key={i}>
                {sec.title && <h3 className="text-sm font-semibold text-foreground mb-2">{sec.title}</h3>}
                <div className="text-sm text-muted-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sec.content) }} />
              </div>
            ))}
          </div>
          <div className="px-5 py-3 border-t border-border flex justify-end">
            <button
              disabled={policy.scroll_to_end_required && !scrolledToEnd}
              onClick={() => setStep(2)}
              className="px-4 py-2 text-sm font-medium text-foreground bg-amber-500 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-600 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Acknowledge */}
      {step === 2 && (
        <div className="glass rounded-xl px-5 py-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border" />
            <span className="text-sm text-foreground">I have read this document and understand its contents.</span>
          </label>

          {(policy.typed_name_required || policy.acknowledgement_type !== 'view_only') && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Type your full name</label>
              <input
                type="text"
                value={typedName}
                onChange={e => setTypedName(e.target.value)}
                placeholder="Your full name"
                className="w-full px-3 py-2 text-sm bg-surface-2 border border-border rounded-lg text-foreground"
              />
            </div>
          )}

          {policy.acknowledgement_type === 'attestation' && attestationQuestions.map((q, i) => (
            <div key={i}>
              <p className="text-sm font-medium text-foreground mb-2">{q.question}</p>
              {q.answer_type === 'yes_no' ? (
                <div className="flex gap-4">
                  {['Yes', 'No'].map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name={`q${i}`} value={opt} checked={answers[i] === opt} onChange={() => setAnswers(p => ({ ...p, [i]: opt }))} className="h-4 w-4" />
                      <span className="text-sm text-foreground">{opt}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <input type="text" value={answers[i] ?? ''} onChange={e => setAnswers(p => ({ ...p, [i]: e.target.value }))} className="w-full px-3 py-2 text-sm bg-surface-2 border border-border rounded-lg text-foreground" />
              )}
            </div>
          ))}

          <div className="flex items-center justify-between pt-2">
            <button onClick={() => setStep(1)} className="text-sm text-muted-foreground hover:text-foreground">Back</button>
            <button disabled={!canSubmitStep2()} onClick={() => setStep(3)} className="px-4 py-2 text-sm font-medium text-foreground bg-amber-500 rounded-lg disabled:opacity-40 hover:bg-amber-600">Continue</button>
          </div>
        </div>
      )}

      {/* Step 3: Confirm */}
      {step === 3 && (
        <div className="glass rounded-xl px-5 py-5 space-y-4">
          <div className="bg-surface-2 rounded-lg p-4">
            <p className="text-sm font-medium text-foreground">You are signing <strong>{policy.name}</strong> v{policy.current_version}</p>
            {typedName && <p className="text-sm text-muted-foreground mt-1">Signing as: <strong>{typedName}</strong></p>}
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            By clicking <strong>Sign Policy</strong>, you confirm you have read this document and agree to its terms.
          </p>
          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400">{error}</div>}
          <div className="flex items-center justify-between pt-1">
            <button onClick={() => setStep(2)} className="text-sm text-muted-foreground hover:text-foreground">Back</button>
            <button disabled={submitting} onClick={() => void handleSubmit()} className="px-4 py-2 text-sm font-semibold text-foreground bg-amber-500 rounded-lg disabled:opacity-50 hover:bg-amber-600">
              {submitting ? 'Signing…' : 'Sign Policy'}
            </button>
          </div>
        </div>
      )}

      {/* Done */}
      {step === 'done' && (
        <div className="glass rounded-xl px-5 py-10 text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
          <div>
            <h2 className="text-lg font-bold text-foreground">Policy Signed</h2>
            <p className="text-sm text-muted-foreground mt-1">{policy.name}</p>
            {signedAt && <p className="text-xs text-muted-foreground mt-1">Signed {new Date(signedAt).toLocaleString()}</p>}
          </div>
          <p className="text-xs text-muted-foreground">Returning to Policies in a moment…</p>
          <button onClick={() => navigate('/policies')} className="text-sm text-amber-500 hover:text-amber-400 font-medium">Back to Policies</button>
        </div>
      )}
    </div>
  );
}
